// The bookkeeping a multi-patient batch needs on the client, kept OUT of the
// component so it can be driven directly by a test with the shapes the server
// actually returns.
//
// Both bugs this closes were about identity. A card was marked sent by its
// POSITION in `resolutions`, and `resolutions` shrinks as patients finish — so
// when Sarah's card completed and David's failed, David arrived at index 0 and
// inherited Sarah's "sent" mark: his failed card read "drafted in Elation" and
// lost the controls he needed to retry it. And a card was marked sent when the
// batch reported `created > 0`, which is a COUNT — one success and one failure
// on the same card marked both orders sent.
//
// So: a card is identified by its `sessionId`, never by where it sits in an
// array; an order is marked sent only when the server names ITS index; and the
// rendered entries are MERGED by session, never replaced by the shrunken list.
//
// Module format: CommonJS (module.exports), like lib/activity-log.js — Jest
// reads this file directly and esbuild interops it for the bundle.

// The key a per-card result is stored under. A single-patient turn keeps the
// bare order index it has always used, so nothing about today's behaviour — or
// a map already in a rendered turn — moves.
function cardSlot(sessionId, orderIndex) {
  return sessionId == null ? String(orderIndex) : `${sessionId}:${orderIndex}`;
}

// Is this reply about a batch at all? A `batchId` plus a `resolutions` ARRAY,
// and nothing else. The array's LENGTH is only how many cards are left — an
// empty one is a real answer — and the scalar `state` is the BATCH's aggregate,
// so one sibling's open question makes it a question state while another
// sibling's card is still on screen. Gating on either hid the whole batch
// (R-S5-45).
function isBatchReply(data) {
  return !!(data && data.batchId != null && Array.isArray(data.resolutions));
}

// The merge R-S5-44 states, in one place, because every route states the same
// three fields and the rule is about the fields, not about the route.
//
// The response carries the entries still OPEN — a finished card drops out of
// it. REPLACING the rendered array with that list renumbers every card, which
// is how a failed patient inherited a filed sibling's slot. Merge by session
// instead: a returning entry is refreshed in place, the rest stay where they
// are, and a card that finished keeps its position and its badge.
//
// But "dropped out" covers two different fates, and keeping both was wrong in
// one of them: a card the provider CANCELLED is gone, and leaving it rendered
// left live Send controls over a session that answers 409 (codex). The server
// states what became of each sibling; a cancelled or expired one is removed,
// a completed one stays for its badge, and one the server does not mention is
// left alone rather than guessed at. An empty batch is a real outcome — the
// old array is never restored over it.
//
// `adopt` is the one difference between the two callers. A /batch-confirm
// answers about the very turn its button sits on, so an entry that turn does
// not hold yet belongs on it. An ordinary reply answers about the whole thread:
// a patient who JOINS the batch is carded on the turn that staged her, and
// adopting her onto every earlier turn would offer the same order N times.
function _mergedBatch(turn, data, { adopt = false } = {}) {
  const open = Array.isArray(data && data.resolutions) ? data.resolutions : [];
  const byId = new Map(open.map((e) => [String(e.sessionId), e]));
  const stateOf = new Map((Array.isArray(data && data.sessions) ? data.sessions : [])
    .filter((r) => r && r.sessionId != null)
    .map((r) => [String(r.sessionId), r.state]));
  const kept = (turn && Array.isArray(turn.batch) ? turn.batch : [])
    .filter((e) => !GONE_STATES.has(stateOf.get(String(e.sessionId))))
    .map((e) => byId.get(String(e.sessionId)) || e);
  if (!adopt) return kept;
  const keptIds = new Set(kept.map((e) => String(e.sessionId)));
  for (const e of open) if (!keptIds.has(String(e.sessionId))) kept.push(e);
  return kept;
}

// The Elation ids the SESSION durably holds, keyed the way the card reads them.
// This is what puts "✓ drafted in Elation #<id>" on a card a batch filed — on
// the /send path too, where there is no `createdIndices` to read (R-S5-48). The
// marker names its own order index; the position of the card in the rendered
// array is not consulted, because that array shrinks as patients finish.
function _markFromMarkers(sent, data) {
  for (const s of (Array.isArray(data && data.sessions) ? data.sessions : [])) {
    if (!s || s.sessionId == null) continue;
    for (const m of (Array.isArray(s.orderIds) ? s.orderIds : [])) {
      if (!m || !Number.isInteger(m.orderIndex)) continue;
      const slot = cardSlot(s.sessionId, m.orderIndex);
      sent[slot] = { ...(sent[slot] || { sent: false }), ...(m.elationId != null ? { elationId: m.elationId } : {}) };
    }
  }
  return sent;
}

// What became of ONE patient, in the words the design record states. The chat
// bubble (`batchResultLines`) says the same thing in the thread; this says it on
// the card, where the provider is looking (O20).
function _outcomeOf(r) {
  if (r.error) return { kind: 'failed', line: "Couldn't be filed — the card is still open to retry." };
  if (r.skipped === 'busy') return { kind: 'busy', line: 'Already being sent — say undo once it finishes to retract.' };
  if (r.skipped) return { kind: 'skipped', line: 'Still needs an answer above — nothing was sent.' };
  const filed = Number.isInteger(r.created) ? r.created : 0;
  const failed = Number.isInteger(r.failed) ? r.failed : 0;
  if (failed) return { kind: 'failed', line: `${failed} of ${filed + failed} couldn't be filed — the card is still open to retry.` };
  return { kind: 'filed', line: `✓ ${filed} order${filed === 1 ? '' : 's'} drafted in Elation` };
}

// The aggregate, always recomputed FROM the settled map — never carried from
// the click that produced it. Counts only.
function _summaryOf(bySession) {
  const ids = Object.keys(bySession);
  let filed = 0; let skipped = 0; let failed = 0;
  for (const id of ids) {
    const k = bySession[id].kind;
    if (k === 'filed') filed += 1;
    else if (k === 'failed') failed += 1;
    else skipped += 1;
  }
  return `${filed} of ${ids.length} patients filed · ${skipped} skipped · ${failed} failed`;
}

// Per patient, plus the aggregate. Keyed by `sessionId`, never by position:
// `results[]` walks batch_seq order while the rendered array shrinks as patients
// finish, so the second result and the second card are routinely different
// patients — the identity rule this whole file exists for. The aggregate is
// COUNTS only.
function batchOutcomes(data) {
  const results = (Array.isArray(data && data.results) ? data.results : []).filter((r) => r && r.sessionId != null);
  if (!results.length) return null;
  const bySession = {};
  for (const r of results) bySession[String(r.sessionId)] = _outcomeOf(r);
  return { bySession, summary: _summaryOf(bySession) };
}

// A card's line is a fact about THAT session, settled by the latest evidence —
// never a snapshot of one click. A second whole-batch confirm reports only the
// rows it could still walk (`confirmBatch` skips terminal ones), so REPLACING
// the map dropped the sibling the first click had filed: her collapsed card
// lost "✓ 1 order drafted in Elation" and the aggregate said "1 of 1". Merge
// instead — the sessions this reply names take their new line, the rest keep
// theirs — and recompute the aggregate from what is left.
function _mergeOutcomes(prev, next) {
  if (!prev || !prev.bySession) return next;
  if (!next || !next.bySession) return prev;
  const bySession = { ...prev.bySession, ...next.bySession };
  return { bySession, summary: _summaryOf(bySession) };
}

// Fold a /batch-confirm response into the turn. Pure: returns a NEW turn.
//   data.results[]   { sessionId, created, createdIndices: [orderIndex], failed, skipped, error }
//   data.resolutions the entries still OPEN afterwards (a finished card drops out)
//   data.sessions[]  { sessionId, batchSeq, state, patient, orderIds } for EVERY
//                    sibling — ids, states and the card header's own DTO (O63)
function markBatchResults(turn, data) {
  const results = Array.isArray(data && data.results) ? data.results : [];
  const sent = _markFromMarkers({ ...((turn && turn.sentOrders) || {}) }, data);
  for (const r of results) {
    if (!r || r.skipped || r.error || !r.sessionId) continue;
    // ONLY the orders the primitive actually filed. `created` is a count and
    // cannot say which; `createdIndices` can.
    for (const orderIndex of (Array.isArray(r.createdIndices) ? r.createdIndices : [])) {
      if (!Number.isInteger(orderIndex)) continue;
      sent[cardSlot(r.sessionId, orderIndex)] = sent[cardSlot(r.sessionId, orderIndex)] || { sent: false };
    }
  }
  // A /batch-confirm carries the roster like every other reply, so what is
  // already on the turn is SETTLED against it before this click's results merge
  // over the top. `confirmBatch` skips terminal rows, so a sibling somebody
  // filed in another tab — or cancelled — is absent from `results[]`, and
  // merging alone left her old failure line standing: beside her own "✓ drafted
  // in Elation #…", or as a ghost counting in the aggregate after her card had
  // gone (codex, real PG). The ORDER is the fix: a row THIS click failed again
  // is confirmable in the roster, so the settle drops its old line and the
  // merge puts the new one back; settling afterwards would drop that too.
  const { outcomes: had, ...rest } = turn || {};
  const outcomes = _mergeOutcomes(had ? _freshOutcomes(had, data) : null, batchOutcomes(data));
  return {
    ...rest,
    sentOrders: sent,
    batch: _mergedBatch(turn, data, { adopt: true }),
    ...(Array.isArray(data && data.sessions) ? { sessions: data.sessions } : {}),
    // Dropped and re-added, never written unconditionally: a reply that reports
    // nothing leaves no `outcomes` key at all, and a map that settled to nothing
    // does not linger under the spread.
    ...(outcomes ? { outcomes } : {}),
  };
}

// Reconcile ONE already-rendered turn against a reply about its batch — the
// same rule, on every route (R-S5-47). /send used to append and never look
// back, so after "no for Sarah" her card kept a live Save draft on the turn it
// was drawn on, one click from a 409; after "cancel all" the whole batch stayed
// on screen. A turn from a different batch, or a reply that is not about a
// batch, is handed back untouched — identically, so nothing re-renders.
function reconcileBatchTurn(turn, data) {
  if (!isBatchReply(data)) return turn;
  if (!turn || turn.batchId == null || !Array.isArray(turn.batch)) return turn;
  if (String(turn.batchId) !== String(data.batchId)) return turn;
  const { outcomes: had, ...rest } = turn;
  const settled = had ? _freshOutcomes(had, data) : null;
  return {
    ...rest,
    sentOrders: _markFromMarkers({ ...(turn.sentOrders || {}) }, data),
    batch: _mergedBatch(turn, data),
    ...(Array.isArray(data.sessions) ? { sessions: data.sessions } : {}),
    ...(settled ? { outcomes: settled } : {}),
  };
}

// …and the roster settles it on every later reply. Three fates, one rule:
//
//   confirmable again   the question got answered, the failed card is back —
//                       "nothing was sent" is a stale sentence sitting under a
//                       live card, so it goes.
//   completed           whatever the last click said, this card FILED. A
//                       successful per-card retry after a failed batch confirm
//                       was leaving "1 of 1 couldn't be filed — the card is
//                       still open to retry" beside "✓ drafted in Elation #…"
//                       (codex, real PG) — a contradiction on the surface that
//                       decides whether to re-order. The line is rebuilt from
//                       the row's own markers, the one source for what filed
//                       (R-S5-48).
//   gone                cancelled or expired: the line leaves with the card.
//
// A sibling the reply does not mention keeps hers untouched (R-S5-44 / R-S5-47).
function _freshOutcomes(outcomes, data) {
  const roster = (Array.isArray(data && data.sessions) ? data.sessions : [])
    .filter((s) => s && s.sessionId != null);
  const stateOf = new Map(roster.map((s) => [String(s.sessionId), s.state]));
  const markersOf = new Map(roster.map((s) => [String(s.sessionId), Array.isArray(s.orderIds) ? s.orderIds : []]));
  const bySession = {};
  for (const id of Object.keys(outcomes.bySession || {})) {
    const state = stateOf.get(id);
    if (CONFIRMABLE_STATES.has(state) || GONE_STATES.has(state)) continue;
    const line = outcomes.bySession[id];
    if (state === 'completed' && line.kind !== 'filed') {
      const filed = (markersOf.get(id) || []).length;
      bySession[id] = { kind: 'filed', line: `✓ ${filed} order${filed === 1 ? '' : 's'} drafted in Elation` };
      continue;
    }
    bySession[id] = line;
  }
  // Nothing survived: there is no report to draw. Writing the empty map put
  // "0 of 0 patients filed · 0 skipped · 0 failed" above two live cards the
  // instant a click failed all of them and any reply came back — and above
  // nothing at all after "cancel all".
  if (!Object.keys(bySession).length) return null;
  return { bySession, summary: _summaryOf(bySession) };
}

// …and across the whole thread, which is where the rule actually lives: EVERY
// rendered turn of the same batch, on EVERY reply.
function reconcileBatchTurns(turns, data) {
  const list = Array.isArray(turns) ? turns : [];
  if (!isBatchReply(data)) return list;
  return list.map((t) => reconcileBatchTurn(t, data));
}

// Write a card's refreshed resolution back into the entry it came from — by
// SESSION, like every other write into a rendered batch.
//
// The edit that produces it is a round-trip: the card's session is fixed before
// the request goes out, the array it sits in is not. Every reply about the batch
// now reconciles the turns already on screen, so between the request and its
// answer a cancelled sibling can leave `turn.batch` and every entry after her
// shifts up — and a positional write-back then put one patient's tests under the
// next patient's header. Nothing wrong reaches a chart (the controls re-read
// their own entry at click time, and a Send-now is preview-guarded), but it is a
// review surface showing one patient's orders under another patient's name,
// which is the thing this arc keeps closing.
//
// A single-patient turn has no `batch` and keeps its scalar `resolution`.
function withResolution(turn, sessionId, resolution, state) {
  const withState = withRosterState(turn, sessionId, state);
  if (!withState || !Array.isArray(withState.batch)) return { ...withState, resolution };
  return {
    ...withState,
    batch: withState.batch.map((e) => (String(e.sessionId) === String(sessionId) ? { ...e, resolution } : e)),
  };
}

// The roster's own word about ONE sibling, from the reply that moved it (L71).
//
// `confirmableSessionIds` — and therefore "Confirm all reviewed cards", its
// denominator and its enabled state (O78) — reads `turn.sessions[].state`, which
// only a `/send`-class reply used to refresh. A card pick goes through
// `/order-edit`, which returns the row's new `state` and reconciles nothing: two
// siblings could both be picked and the button still read "0 of 2", refusing a
// confirm the server would now accept. The route already answers with the fact;
// this is the page believing it, by the same rule every other reply about a
// batch follows (R-S5-47). Unknown sibling, no state, no roster → unchanged.
function withRosterState(turn, sessionId, state) {
  if (!turn || !state || !Array.isArray(turn.sessions)) return turn;
  let moved = false;
  const sessions = turn.sessions.map((s) => {
    if (!s || s.sessionId == null || String(s.sessionId) !== String(sessionId)) return s;
    if (s.state === state) return s;
    moved = true;
    return { ...s, state };
  });
  return moved ? { ...turn, sessions } : turn;
}

// Gone from the thread, not merely finished: nothing to render, nothing to send.
const GONE_STATES = new Set(['cancelled', 'expired']);

// A row nothing more will be said to: it finished, or it left.
const TERMINAL_ROW_STATES = new Set(['completed', 'cancelled', 'expired']);

// Is this conversation still one the next message can continue?
//
// The server's own live set, read here rather than re-spelled:
// `TERMINAL_SESSION_STATES` is completed | cancelled | expired, and EVERY other
// state — `awaiting_*`, `partially_sent`, `processing` — is a row it will still
// route a message to. The page used to spell its own list ("the state starts
// with `awaiting`"), written in 07-23, before `partially_sent` existed (#688,
// 09-04). The two drifted the day that state was added: a History resume onto a
// half-sent or in-flight card installed the row `/card` built for that handle
// and then held NO pin, so the tap went out unpinned, landed on whatever thread
// sorted newest, and filed THAT thread's unreviewed card while the resumed one
// stayed half-sent (codex r2 MAJOR 1; the adjudicator reproduced it on both
// legs at real PG, batch and single-patient alike). One predicate over the one
// terminal set, so a client liveness test and the server's cannot drift again
// (R-S5-51 amended; PR-S5b-6).
function rowIsLive(state) {
  return typeof state === 'string' && !TERMINAL_ROW_STATES.has(state);
}

// Does THIS reply's roster say the row the page is pinned to is over?
//
// The page used to drop its History pin whenever the batch it had just
// confirmed completed — which says nothing about a pin held on some other
// conversation. Once the chips became the PIN's chips that turned into a
// wrong-chart path: the row read "David Levy · 1", the pin died in the same
// breath, and the tap went out unpinned to whatever row sorted active, staging
// that one for Sarah (codex, real PG). A pin is dropped when its OWN row is
// done: the roster names it terminal. A roster that does not mention it — a
// reply about a different batch — leaves it exactly as it was.
function pinnedRowTerminal(data, pin) {
  if (pin == null) return false;
  const row = (Array.isArray(data && data.sessions) ? data.sessions : [])
    .find((s) => s && s.sessionId != null && String(s.sessionId) === String(pin));
  return !!(row && TERMINAL_ROW_STATES.has(row.state));
}

// May this reply's chip row be installed over the one on screen?
//
// Every reply that can carry chips names the session those chips answer for
// (`quickRepliesFor` — the session the page's next message will reach). A row
// built for some OTHER session is left alone, because the row on screen already
// holds this page's own pin's chips: a numbered chip is POSITIONAL, so a row
// from another conversation reads "David Levy · 1" while the digit it posts
// lands where 1 means Sarah — a pending card under the wrong chart, one confirm
// from a wrong-chart order (codex, real PG, on `/batch-confirm`; the
// adjudicator's ADJ4C-2, on `/send`). An UNOWNED row (`null`) is installed:
// there is nothing to disagree with, and every single-patient turn is one. So is
// any row while the page is unpinned, because then the reply's owner IS where
// the next message goes. One rule, both callers (R-S5-52 amended; PR-S5b-6).
function chipRowIsOurs(data, pin) {
  const owner = data && data.quickRepliesFor != null ? String(data.quickRepliesFor) : null;
  const held = pin == null ? null : String(pin);
  return owner === null || held === null || owner === held;
}

// The states in which a sibling has a card the provider can still act on — the
// same set `toBatchEntries` admits into `resolutions[]`. Stated once here and
// read by the opened tracker too, so the review gate's denominator and the
// server's `live.ready` cannot drift apart (O78).
const CONFIRMABLE_STATES = new Set(['awaiting_confirmation', 'partially_sent']);

// One line per patient, in the order the server walked them.
//
// Named from ONE map, built the way everything else after a /batch-confirm is:
// the turn's own entries first, then the reply's roster over them. The roster
// carries the card-header DTO for EVERY sibling, terminal ones included
// (R-S5-46), and the turn's entries do not — so a patient who joined the batch
// after this turn was drawn read as "that patient" in the bubble under the very
// click that filed her order. Anonymous, never another patient's name (the
// lookup is by `sessionId`), but the sentence a provider reads after the button
// r2 made honest has to name everyone the click walked.
function _namesFor(turn, data) {
  const named = new Map();
  const put = (id, patient) => {
    if (id == null || !patient) return;
    const name = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    if (name) named.set(String(id), name);
  };
  for (const e of ((turn && Array.isArray(turn.batch)) ? turn.batch : [])) put(e && e.sessionId, e && e.patient);
  for (const s of (Array.isArray(data && data.sessions) ? data.sessions : [])) put(s && s.sessionId, s && s.patient);
  return named;
}

function batchResultLines(turn, data) {
  const named = _namesFor(turn, data);
  return (Array.isArray(data && data.results) ? data.results : []).map((r) => {
    // …and `that patient` only when no source names her at all.
    const who = named.get(String(r.sessionId)) || 'that patient';
    if (r.error) return `${who}: couldn't be sent — try that card again.`;
    if (r.skipped === 'busy') return `${who}: already being sent.`;
    if (r.skipped) return `${who}: skipped — that card still needs an answer.`;
    return `${who}: ${r.created} order${r.created === 1 ? '' : 's'} filed`
      + (r.failed ? `, ${r.failed} failed — that card is still open to retry.` : '.');
  });
}

module.exports = {
  cardSlot, markBatchResults, batchResultLines, batchOutcomes, CONFIRMABLE_STATES, pinnedRowTerminal,
  chipRowIsOurs, rowIsLive, isBatchReply, reconcileBatchTurn, reconcileBatchTurns, withResolution,
  withRosterState,
};
